import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import List from 'devextreme-react/list';
import Button from 'devextreme-react/button';
import { useAuthService } from '../../services';
import { useGlobalState } from '../../contexts/global-state';
import logomark from '../../imgs/logomark-reverse.svg';
import { buildMenuGroups, findSelectedPath } from './menu-groups';
import './side-navigation-menu.scss';

export default function ({ compactMode, openMenu, onLogoClick, onNavigate }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getActiveAccount, signOut } = useAuthService();
  const { managedSchools, committees } = useGlobalState();
  const { idTokenClaims: { givenName, surname, memberId } } = getActiveAccount() || { idTokenClaims: {} };

  const groups = useMemo(
    () => buildMenuGroups(memberId, managedSchools, committees),
    [memberId, managedSchools, committees]
  );
  const selectedPath = findSelectedPath(groups, pathname);

  function onItemClick(e) {
    // In the collapsed icon rail, a click expands the menu rather than navigating,
    // since the icons alone don't identify which school or committee is which.
    if (compactMode) {
      openMenu();
      return;
    }
    navigate(e.itemData.path);
    onNavigate();
  }

  return (
    <div className={`side-navigation-menu dx-swatch-additional${compactMode ? ' compact' : ''}`}>
      {/* The app icon expands/collapses the menu (the header button only exists on phones). */}
      <div
        className="menu-user"
        role="button"
        tabIndex={0}
        aria-label={compactMode ? 'Expand menu' : 'Collapse menu'}
        aria-expanded={!compactMode}
        title={compactMode ? 'Expand menu' : 'Collapse menu'}
        onClick={onLogoClick}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onLogoClick();
          }
        }}
      >
        <img className="menu-logomark" src={logomark} alt="Edmonton Public Teachers" />
        <div className="menu-user-name">
          <div>{givenName}</div>
          <div>{surname}</div>
        </div>
      </div>
      {/* Only the list scrolls, so the footer stays pinned to the bottom of the panel. */}
      <div className="menu-scroll">
        <List
          className="menu-list"
          dataSource={groups}
          grouped
          selectionMode="none"
          focusStateEnabled={false}
          onItemClick={onItemClick}
          groupRender={group => (
            <div className="menu-group" title={group.key}>
              <span className="menu-group-text">{group.key}</span>
            </div>
          )}
          itemRender={item => (
            <div
              className={`menu-item${item.path === selectedPath ? ' menu-item-selected' : ''}`}
              title={item.text}
            >
              <i className={`menu-item-icon ${item.icon}`} />
              <span className="menu-item-text">{item.text}</span>
            </div>
          )}
        />
      </div>
      <div className="menu-footer">
        <Button
          text={compactMode ? '' : 'Sign Out'}
          hint="Sign Out"
          icon="fa-solid fa-right-from-bracket"
          type="default"
          stylingMode="contained"
          width={compactMode ? 44 : '100%'}
          onClick={() => {
            navigate('/signed-out');
            signOut();
          }}
        />
      </div>
    </div>
  );
}
